import { BadgeProps, tokens } from "@blurb/codex-react";
import { awsRum } from "../../rum/cloudwatchRum";

export type StatusBadgeColor = NonNullable<BadgeProps["color"]>;

export type StatusBadgeDisplay = {
  name: string;
  color: StatusBadgeColor;
};

const ICON_COLOR: Record<StatusBadgeColor, string> = {
  blue: tokens.color.primitive.light.blue[700],
  purple: tokens.color.primitive.light.purple[700],
  green: tokens.color.primitive.light.green[700],
  orange: tokens.color.primitive.light.yellow[800],
  red: tokens.color.primitive.light.red[700],
  white: tokens.color.primitive.light.gray[900],
  gray: tokens.color.primitive.light.gray[700],
};

/** Token icon color matching a Badge semantic color. */
export function statusBadgeIconColor(color: StatusBadgeColor): string {
  return ICON_COLOR[color];
}

const reportedUnrecognized = new Set<string>();

// Reports an unrecognized status to RUM once per (kind, value) per session — deduped so a value
// appearing across many table rows and re-renders can't spam the JS-error rate alarm. Routed through
// recordError so the gap still lands on that alarm: a status the backend emits but this bundle doesn't
// know is something we want to be notified about, not silently degrade.
function reportUnrecognizedStatus(kind: string, status: string): void {
  const key = `${kind}:${status}`;
  if (reportedUnrecognized.has(key)) return;
  reportedUnrecognized.add(key);
  try {
    awsRum?.recordError(new Error(`Unrecognized ${kind} value from backend: ${status}`));
  } catch {
    // Telemetry must never crash the cell — this fallback exists precisely to avoid a render crash.
  }
}

// Resolves the badge for a status, falling back to a neutral gray badge (labeled with the raw value)
// for a status this build doesn't know — e.g. one the backend adds after this bundle ships — so the
// cell renders instead of crashing. Reports the gap to RUM (deduped); `kind` labels it there.
export function resolveStatusBadge<T extends string>(
  config: Record<T, StatusBadgeDisplay>,
  kind: string,
  status: T
): StatusBadgeDisplay {
  const known: StatusBadgeDisplay | undefined = config[status];
  if (known) return known;
  reportUnrecognizedStatus(kind, status);
  return { name: status, color: "gray" };
}
